import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import CalendlyCTA from "@/components/CalendlyCTA";

const BEN_PHOTO =
  "https://customer-assets.emergentagent.com/job_landing-hub-595/artifacts/h8mva8ew_IMG_0169.jpeg";

export default function MeetBen() {
  return (
    <section
      id="meet-ben"
      data-testid="meet-ben-section"
      className="relative"
      style={{ backgroundColor: "#F7F5F0" }}
    >
      {/* Full-width environmental photo */}
      <div
        className="relative w-full overflow-hidden"
        data-testid="ben-photo"
        style={{ height: "min(85vh, 820px)", minHeight: "480px" }}
      >
        <img
          src={BEN_PHOTO}
          alt="Ben — founder of Anchor — sitting in an open meadow under a wide sky"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "65% 88%" }}
          loading="lazy"
        />
        {/* Subtle vignette at top so nav stays legible */}
        <div
          className="absolute inset-x-0 top-0 h-24 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(247,245,240,0.4) 0%, rgba(247,245,240,0) 100%)",
          }}
        />
        {/* Caption */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <div
            className="max-w-7xl mx-auto inline-block px-4 py-2"
            style={{ backgroundColor: "rgba(21,36,61,0.55)", backdropFilter: "blur(4px)" }}
          >
            <p className="text-[11px] tracking-[0.28em] uppercase text-white">
              Ben · Founder, Anchor · ICF Certified Coach
            </p>
          </div>
        </div>
      </div>

      {/* Story */}
      <div className="py-20 md:py-32 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
            <div className="md:col-span-4">
              <p className="overline mb-6">05 — The teacher</p>
              <h2
                className="serif text-5xl md:text-6xl text-[#15243D] leading-[1.05] tracking-tight"
                style={{ fontWeight: 300 }}
              >
                Meet <em className="italic text-[#C07865]">Ben.</em>
              </h2>
            </div>

            <div className="md:col-span-8">
              <div className="space-y-6 text-lg font-light leading-relaxed text-[#5A5D63]">
                <p className="text-[#15243D]">
                  Anchor was born during one of the most challenging seasons of
                  my life.
                </p>
                <p>
                  While navigating business collapse, financial uncertainty,
                  family caregiving, and time away from my sons, I discovered
                  something unexpected.
                </p>

                <blockquote
                  className="serif italic text-2xl md:text-3xl text-[#15243D] leading-snug border-l-2 border-[#C07865] pl-6 py-1 my-8"
                  style={{ fontWeight: 300 }}
                >
                  The greatest challenge wasn’t what was happening around me.
                  It was how easily I could be pulled away from myself.
                </blockquote>

                <p>
                  Like many people, I thought the answer was more information,
                  better strategies, or harder work.
                </p>
                <p>
                  What I found instead was something much simpler:{" "}
                  <span className="serif italic text-[#15243D]">presence</span> —
                  the ability to pause, return to the present moment, and
                  respond rather than react.
                </p>

                <div className="pt-8 mt-4 border-t border-[#15243D]/10 space-y-6">
                  <p>
                    Over time, I realized that most people don’t have a life
                    problem.
                  </p>
                  <p
                    className="serif italic text-2xl md:text-3xl text-[#C07865] leading-snug"
                    style={{ fontWeight: 300 }}
                  >
                    They have a relationship-to-what’s-happening problem.
                  </p>
                  <p>
                    Anchor was created to help people navigate life’s waves
                    without being consumed by them — through simple practices
                    of awareness, presence, and intentional action.
                  </p>
                </div>

                <div className="pt-8 mt-4 border-t border-[#15243D]/10">
                  <p
                    className="serif text-2xl md:text-3xl text-[#15243D] leading-snug"
                    style={{ fontWeight: 300 }}
                  >
                    Strength doesn’t begin when life gets easier.
                  </p>
                  <p
                    className="serif text-2xl md:text-3xl italic text-[#C07865] leading-snug mt-2"
                    style={{ fontWeight: 300 }}
                  >
                    Strength begins in stillness.
                  </p>
                </div>

                <div className="pt-8 mt-4 border-t border-[#15243D]/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                  <Link
                    to="/story"
                    data-testid="meet-ben-read-full-story"
                    className="group inline-flex items-center gap-3 text-sm tracking-[0.18em] uppercase text-[#15243D] hover:text-[#C07865] transition-colors"
                  >
                    Read the full story
                    <ArrowUpRight
                      size={14}
                      strokeWidth={1.5}
                      className="group-hover:translate-x-0.5 transition-transform"
                    />
                  </Link>
                  <CalendlyCTA
                    variant="inline"
                    testId="meet-ben-calendly"
                    label="Talk with Ben — 30 min"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
